// Prompt construction. Pure. Turns an automation entry into the
// prompt object the EffectPromptDrawer renders. GameBoard handles
// queueing and confirm/cancel side effects.
//
// We NEVER auto-generate description text from the effect. The card art
// and its rules text are already shown in the drawer, so the player
// already sees the correct wording. A prompt carries body text only when
// it's intentionally authored. The entry's own `description`, or a
// REMINDER op's explicit `text`. The op still drives the promptType
// (choice / draw / reminder / confirm) and any choice buttons, since those
// are functional, not descriptive.

import { OP } from './schema';
import { WITCH_CURSE_CHOICES } from './ops/cards/witchCurse';

/**
 * Build one prompt object for an automation entry. An entry may carry
 * multiple effects in `entry.effect[]`; the resulting prompt fires them
 * all on a single Confirm. Choice / draw / reminder use specialised
 * promptTypes the drawer renders with distinct buttons.
 *
 * @param {Object} cardInstance - the live card on the table (or, for
 *   equipped artifacts, the equipment data in the host's array).
 * @param {Object} fullCard - the catalog entry (for the card name).
 * @param {Object} entry - the matching automation entry.
 * @param {'p1'|'p2'} player - the effective controller for this firing.
 * @returns {Object|null} prompt object, or null if the entry has no effects.
 */
export function buildPromptForEntry(cardInstance, fullCard, entry, player) {
  const first = entry.effect?.[0];
  if (!first) return null;

  const base = {
    id: `prompt-${cardInstance.id}-${Math.random().toString(36).slice(2, 8)}`,
    cardInstance,
    cardName: fullCard?.name || cardInstance.cardId,
    controller: player,
    sourceEntry: entry,
    description: entry.description || null,
  };

  if (first.op === OP.CHOICE_GRANT_THRESHOLD) {
    const options = first.args?.options || [];
    const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
    return { ...base, promptType: 'choice', choices: options.map((opt) => ({ label: cap(opt), value: opt })) };
  }

  if (first.op === OP.WITCH_CURSE) {
    return { ...base, promptType: 'witch_curse_choice', choices: [...WITCH_CURSE_CHOICES] };
  }

  if (first.op === OP.SEER_SCRY_CHOICE) {
    return {
      ...base,
      promptType: 'seer_scry_choice',
      choices: [
        { label: 'Top spell', value: 'spellbook' },
        { label: 'Top site',  value: 'atlas' },
      ],
    };
  }

  if (first.op === OP.PERSECUTOR_ACTION) {
    return {
      ...base,
      promptType: 'persecutor_choice',
      choices: [
        { label: 'Step toward Evil', value: 'step' },
        { label: 'Brand an enemy',   value: 'brand' },
      ],
    };
  }

  if (first.op === OP.PACT_SACRIFICE_DRAW) {
    return {
      ...base,
      promptType: 'pact_choice',
      choices: [
        { label: 'Sacrifice the caster', value: 'sacrifice' },
        { label: 'Lose half your life',  value: 'life' },
      ],
    };
  }

  // A REMINDER's text IS the authored body. The only effect whose text
  // lives on the op itself rather than the entry.
  if (first.op === OP.REMINDER) {
    return { ...base, promptType: 'reminder', description: first.args?.text || entry.description || null };
  }

  if (first.op === OP.DRAW) {
    return { ...base, promptType: 'draw' };
  }

  return { ...base, promptType: 'confirm' };
}
